<script setup>
  import { ref, watch, computed } from 'vue'

  import Icon from '@martyrs/src/design-system/components/Icon/Icon.vue';

  const props = defineProps({
    label: String,
    name: String,
    value: String,
    radio: {
      type: [Array, Boolean, Object],
      default: () => []
    },
    theme: {
      type: String,
      default: "light"
    },
    mode: {
      type: String,
      default: "switch",
      validator: (v) => ["switch", "checkbox"].includes(v)
    },
    // Недоступный контрол: input не переключается, строка приглушена.
    disabled: {
      type: Boolean,
      default: false
    },
    // Размер квадрата в режиме checkbox: m — 2r, s — 1.5r (список фильтров
    // рядом с текстом footnote).
    size: {
      type: String,
      default: 'm',
      validator: (v) => ['s', 'm'].includes(v)
    }
  })
  const emit = defineEmits(['update:radio'])
  const updateInputText = (event) => {
    if (Array.isArray(props.radio)) {
      let newRadio = [...props.radio];  // создаем копию массива для иммутабельности
      if (event.target.checked) {
        newRadio.push(props.value);
      } else {
        const index = newRadio.indexOf(props.value);
        if (index !== -1) {
          newRadio.splice(index, 1);
        }
      }
      emit('update:radio', newRadio);
    } else if (typeof props.radio === 'boolean') {
      emit('update:radio', event.target.checked);
    }
  }
</script>
<template>
  <label class="flex-v-center flex-nowrap flex" :class="disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'">
    <div v-if="label" class="mn-r-auto opacity-50 mn-r-small">
      <span>{{label}}</span>
    </div>
    <input
      @change="updateInputText"
      class="round radiobutton"
      type="checkbox"
      :name="name"
      :value="value"
      :disabled="disabled"
      :checked="Array.isArray(radio) ? radio.includes(value) : radio"
    >
    <div
      class="cursor-pointer flex-center field-wrapper"
      :class="{
        'mn-l-thin': label,
        'ease-elastic pd-thin h-2r w-4r radius-extra': mode === 'switch',
        // flex-child-none: при подписи в две строки флекс сжимал квадрат в прямоугольник.
        'flex-child-none': mode === 'checkbox',
        // Скругление квадрата — как у поля ввода (.input — thin), в обоих
        // размерах (владелец 2026-10-16).
        'w-2r h-2r radius-thin': mode === 'checkbox' && size === 'm',
        'w-[1.5rem] h-[1.5rem] radius-thin': mode === 'checkbox' && size === 's',
        'bg-inverse': !(Array.isArray(radio) ? radio.includes(value) : radio) && theme === 'dark' && mode === 'switch',
        'br-strong': theme === 'dark' && mode === 'checkbox',
        // Без галочки в светлой теме квадрат — bg-card на карточке: без рамки
        // его не видно (beautify, шаг согласия на съёмку, 2026-09-19).
        'br-1px br-subtle': theme !== 'dark' && mode === 'checkbox' && !(Array.isArray(radio) ? radio.includes(value) : radio),
        'bg-card': !(Array.isArray(radio) ? radio.includes(value) : radio) && theme === 'light',
        'bg-accent': (Array.isArray(radio) ? radio.includes(value) : radio) && mode === 'switch',
        'bg-accent br-accent': (Array.isArray(radio) ? radio.includes(value) : radio) && mode === 'checkbox',
      }"
    >
      <!-- Switch toggle circle -->
      <!-- Ручка свича светлая в ОБЕИХ темах: bg-base в светлой теме и
           bg-inverse в тёмной. Один bg-base пропадал бы на тёмном треке. -->
      <div
        v-if="mode === 'switch'"
        class="ease-linear w-1r h-1r radius-extra bg-base dark:bg-inverse"
        :class="{
          'mn-r-auto': !(Array.isArray(radio) ? radio.includes(value) : radio),
          'mn-l-auto': Array.isArray(radio) ? radio.includes(value) : radio,
        }"
      />
      <!-- Checkbox checkmark -->
      <span
        v-else-if="mode === 'checkbox' && (Array.isArray(radio) ? radio.includes(value) : radio)"
        class="t-inverse w-100 h-100 flex flex-center"
      >
        <Icon name="checkmark" class="i-medium" color="#fff" />
      </span>
    </div>
    <!-- Подпись справа от контрола — внутри того же label: клик по тексту
         переключает контрол сам, своего обработчика у потребителя не нужно.
         (Проп label — подпись слева, приглушённая, для строки настроек.) -->
    <span v-if="$slots.default" class="mn-l-thin">
      <slot />
    </span>
  </label>
</template>
